import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.53 13.53 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

// Password input with a show/hide toggle. The toggle is type="button" so it
// never submits the form.
function PasswordField({ className, disabled, ...inputProps }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        {...inputProps}
        type={visible ? 'text' : 'password'}
        disabled={disabled}
        className={`${className} pr-12`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        className="absolute inset-y-0 right-0 flex items-center px-4 text-[#64748b] hover:text-[#0028CD] focus:outline-none focus-visible:text-[#0028CD] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

// Landing page for the trial invite email. The link carries uid+token, which
// the IdP issued and which are the only credential here - so they are read from
// the URL and posted straight back, never stored or displayed.
function SetPassword() {
  const { uid, token } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      uid: (params.get('uid') || '').trim(),
      token: (params.get('token') || '').trim(),
    };
  }, []);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const linkIsComplete = Boolean(uid && token);

  useEffect(() => {
    if (!linkIsComplete) {
      setError('This link is incomplete. Please open the link from your invite email again.');
    }
  }, [linkIsComplete]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post('/api/invite/set-password', {
        uid,
        token,
        new_password: password,
        confirm_password: confirm,
      });
      setDone(true);
    } catch (err) {
      // The IdP returns a usable sentence for an expired link or a password
      // that fails policy, so it is shown rather than replaced.
      const data = err?.response?.data;
      setError(
        (data && (data.message || data.error_description)) ||
        'We could not set your password. The link may have expired.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-[#f8f9fc] border border-[#e2e8f0] rounded-xl text-[#0a0a0a] ' +
    'focus:outline-none focus:border-[#0028CD] transition-colors';

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-32 pb-20">
        <div className="container max-w-md">
          {done ? (
            <>
              <h1 className="text-4xl font-bold mb-4">You're all set</h1>
              <p className="text-[#64748b] mb-8">
                Your password has been saved. You can now sign in with your email
                address and the password you just chose.
              </p>
              <a
                href="/dashboard?login=1"
                className="inline-block px-6 py-3 bg-[#0028CD] text-white rounded-xl font-semibold hover:bg-[#001F9D] transition-colors"
              >
                Go to sign in
              </a>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold mb-3">Set your password</h1>
              <p className="text-[#64748b] mb-8">
                Choose a password to finish setting up your ADX Hub free trial account.
              </p>

              {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 text-red-800">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="new-password" className="block text-sm text-[#64748b] mb-2">
                    New password *
                  </label>
                  <PasswordField
                    id="new-password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={12}
                    disabled={!linkIsComplete || isSubmitting}
                    className={inputClass}
                  />
                  <p className="mt-2 text-sm text-[#64748b]">
                    At least 12 characters, and not too similar to your email address.
                  </p>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm text-[#64748b] mb-2">
                    Confirm password *
                  </label>
                  <PasswordField
                    id="confirm-password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    disabled={!linkIsComplete || isSubmitting}
                    className={inputClass}
                  />
                </div>

                <button
                  type="submit"
                  disabled={!linkIsComplete || isSubmitting}
                  className="w-full px-6 py-3 bg-[#0028CD] text-white rounded-xl font-semibold hover:bg-[#001F9D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Save password'}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default SetPassword;
